import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { Button } from "../ui/button";
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-screen image lightbox with zoom + pan, used for defect photos on the
 * contractor defect cards. Lets the user zoom into a picture in-page (without
 * navigating away) before leaving the page or printing the report.
 */
export const DefectImageLightbox = ({ images, index, onIndexChange, onClose }) => {
  const open = index !== null && index !== undefined;
  const [scale, setScale] = useState(1);

  // Reset zoom whenever the visible image changes
  useEffect(() => {
    setScale(1);
  }, [index]);

  if (!open) return null;

  const total = images.length;
  const src = images[index];

  const zoomIn = () => setScale((s) => Math.min(Math.round((s + 0.5) * 10) / 10, 4));
  const zoomOut = () => setScale((s) => Math.max(Math.round((s - 0.5) * 10) / 10, 1));
  const toggleZoom = () => setScale((s) => (s === 1 ? 2 : 1));
  const go = (delta) => onIndexChange((index + delta + total) % total);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-[96vw] sm:max-w-5xl p-0 gap-0 bg-slate-950 border-slate-800 overflow-hidden"
        data-testid="defect-image-lightbox"
      >
        {/* Toolbar (kept clear of the dialog's top-right close button) */}
        <div className="flex items-center gap-4 px-4 py-2 pr-12 bg-slate-900/90 text-slate-200">
          <span className="text-sm font-medium" data-testid="lightbox-counter">
            Photo {index + 1} of {total} {scale > 1 ? `· ${scale}×` : ""}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={zoomOut}
              disabled={scale <= 1}
              className="text-slate-200 hover:bg-slate-700 h-8 w-8"
              data-testid="lightbox-zoom-out"
              aria-label="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={zoomIn}
              disabled={scale >= 4}
              className="text-slate-200 hover:bg-slate-700 h-8 w-8"
              data-testid="lightbox-zoom-in"
              aria-label="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Image area (scrolls/pans when zoomed) */}
        <div
          className="relative flex items-center justify-center overflow-auto bg-black"
          style={{ height: "78vh" }}
          data-testid="lightbox-image-area"
        >
          <img
            src={src}
            alt={`Defect photo ${index + 1}`}
            onClick={toggleZoom}
            className="select-none transition-[width] duration-150"
            style={{
              width: scale === 1 ? "auto" : `${scale * 100}%`,
              maxWidth: scale === 1 ? "100%" : "none",
              maxHeight: scale === 1 ? "78vh" : "none",
              objectFit: "contain",
              flexShrink: 0,
              cursor: scale === 1 ? "zoom-in" : "zoom-out",
            }}
            data-testid="lightbox-image"
          />

          {total > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  go(-1);
                }}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-slate-900/70 hover:bg-slate-800 text-white rounded-full p-2"
                data-testid="lightbox-prev"
                aria-label="Previous photo"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  go(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-slate-900/70 hover:bg-slate-800 text-white rounded-full p-2"
                data-testid="lightbox-next"
                aria-label="Next photo"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 py-2 bg-slate-900/90">
          Tap the image to zoom · use + / − to zoom further · scroll to pan
        </p>
      </DialogContent>
    </Dialog>
  );
};
